import { useCallback, useMemo } from "react"
import { PopoverComboboxItem } from "shared-web/components/composite/popover"

import { UsePopoverMenuProps, UsePopoverMenuRes } from "./types"

// usePopoverMenu is a hook with a developer-friendly interface for configuring
// a PopoverMenu component.
function usePopoverMenu<T>({
  items,
  onSelect,
  keyExtractor,
  labelExtractor,
  contentExtractor,
  onChangeQuery,
  footerItem,
  onSelectFooter,
}: UsePopoverMenuProps<T>): UsePopoverMenuRes {
  const comboboxItems = useMemo<PopoverComboboxItem[]>(() => {
    const res: PopoverComboboxItem[] = items.map((item) => ({
      key: keyExtractor(item),
      label: labelExtractor(item),
      content: contentExtractor?.(item),
    }))
    if (footerItem) {
      res.push(footerItem)
    }
    return res
  }, [items, keyExtractor, labelExtractor, contentExtractor, footerItem])

  const onSelectComboboxItem = useCallback(
    (comboboxItem: PopoverComboboxItem) => {
      if (comboboxItem.key === footerItem?.key) {
        onSelectFooter?.()
        return
      }
      const item = items.find((item) => keyExtractor(item) === comboboxItem.key)
      if (!item) return
      onSelect(item)
    },
    [footerItem?.key, items, onSelect, onSelectFooter, keyExtractor]
  )

  return {
    popoverMenuProps: {
      items: comboboxItems,
      onSelect: onSelectComboboxItem,
      onChangeQuery,
    },
  }
}

export default usePopoverMenu
